import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { FormControlLabel } from '@mui/material';
import LeftAngle from '../../assets/icons/left-angle.svg';
import RightAngle from '../../assets/icons/right-angle.svg';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import InfoIcon from '../../assets/icons/info-icon.svg';
import EmptyBigCard from '../dashboard/dashboardCards/emptyBigCard';
import { REFERRER_REPORT } from '../../constants/pageIdentifiers';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import { useGetReferrersReport } from '../../apiHooks/queries/reports/useGetReferrersReport';
import SwitchInput from '../inputs/switchInput';
import AdsReportTable from '../../partials/adsReportTable';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import VatDisplayer from '../../partials/VATDisplayer';
import { useOrganizationSettingsStore } from '../../store/organizationSettingsStore';
import OrganizationType from '../enums/organizationTypesEnum';
import ExportButton from '../exportButton/exportButton';
import { useGetExportReferrerTable } from '../../apiHooks/mutations/reportsExportMutations/useGetExportReferrerTable';
import SearchIcon from '../../assets/icons/searchIconAds.png';
import { useDebounce } from '../../constants/hookConstants';
import ThreeDots from '../../assets/icons/three dots.svg';
import ExportIcon from '../../assets/icons/export-icon.svg';

export const referrerReportColumns: TableColumnInterface[] = [
  {
    field: 'formSubmissions',
    headerName: 'fsCountSeo',
    cellRenderer: (params) => (
      <span>
        {
          params
            ? (Math.round(params)
              ?.toLocaleString())
            : (0)
        }
      </span>
    ),
  },
  {
    field: 'sales',
    headerName: 'sales',
    cellRenderer: (params) => (
      <span>
        {
          params
            ? (Math.round(params)
              ?.toLocaleString())
            : (0)
        }
      </span>
    ),
  },
  {
    field: 'income',
    headerName: 'income',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'cvRateSeo',
    headerName: 'cvRateSeo',
    cellRenderer: (params, rowObject) => (
      <span>
        {rowObject?.sales && rowObject?.formSubmissions && (
          Math.round((rowObject.sales / (rowObject.formSubmissions || 1)) * 100)
        )}
        %
      </span>
    ),
  },
];

export const eCommerceReferrerReportColumns: TableColumnInterface[] = [
  {
    field: 'sales',
    headerName: 'sales',
    cellRenderer: (params) => (
      <span>
        {
          params
            ? (Math.round(params)
              ?.toLocaleString())
            : (0)
        }
      </span>
    ),
  },
  {
    field: 'income',
    headerName: 'income',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
];

export const referrerReportFixedColumns: TableColumnInterface[] = [
  {
    field: 'referrer',
    headerName: 'referrer',
    cellRenderer: (params: string) => (
      <Tooltip
        arrow
        placement="top"
        title={params}
      >
        <span className="max-w-[190px] truncate 2xl:max-w-[250px]">
          {params === '' ? 'Direct' : params}
        </span>
      </Tooltip>
    ),
  },
];

interface ReferrerInterfaceProps{
  adCode?: string ;
}

const ReferrerReport: FC<ReferrerInterfaceProps> = (props) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);
  const [isToggledOriginalReferrer, setIsToggledOriginalReferrer] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const organizationType = useOrganizationSettingsStore((state) => state.organizationSettings?.organizationType);

  const searchRef = useRef<HTMLDivElement>(null);

  const [showSearch, setShowSearch] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
      setShowSearch(false);
    }
  };

  useEffect(() => {
    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch]);

  const toggleSearch = () => {
    setShowSearch((prev) => !prev);
  };

  const debouncedSearch = useDebounce((val: string) => {
    setSearch(val);
  }, 500);

  const {
    data: referrerReport,
    isFetching,
    isError,
  } = useGetReferrersReport(
    REFERRER_REPORT,
    dashboardFilterDateRange[0],
    dashboardFilterDateRange[1],
    page || 0,
    isToggledOriginalReferrer,
    props.adCode,
    search,
  );

  const { mutate: reportDownload } = useGetExportReferrerTable({
    from: dashboardFilterDateRange[0],
    to: dashboardFilterDateRange[1],
    page: page || 0,
    original: isToggledOriginalReferrer,
  });

  const reportData = referrerReport?.data?.data?.report;

  const handlePrevPage = () => {
    setPage(page - 1);
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  if (referrerReport?.data?.data?.report === null && page > 0) {
    setPage(0);
  }

  const toggleReferrer = () => {
    setIsToggledOriginalReferrer(!isToggledOriginalReferrer);
    setPage(0);
  };

  return (
    <div className="flex h-full flex-col text-clip rounded-lg">
      <div
        className="flex max-h-[47px] items-center justify-between border-b border-dark px-3 py-2"
      >
        <div className="flex items-center gap-1">
          <p className="text-lg font-medium leading-[36px] text-primary xl:text-base">
            {t('referrerReportTitle')}
          </p>
          <Tooltip title={t('referrerReportInfo')}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
        <div className="flex items-center">
          <div className="flex items-center ">
            <div className="mx-4 flex w-full items-center justify-between gap-2">
              <div />
              <div
                className="border-gray-300 relative flex items-center rounded border transition-all duration-500 ease-in-out"
              >
                <button
                  onClick={() => {
                    toggleSearch();
                  }}
                  disabled={!reportData || reportData.length === 0 || isFetching || isError}
                  className={`flex h-8 w-8 items-center justify-center rounded transition-all duration-500 ease-in-out 
                  hover:bg-gray-200 ${
                    showSearch ? 'invisible w-0' : 'visible w-8'
                  } ${!reportData || reportData.length === 0 || isFetching || isError ? 'bg-[#EAEAEA] '
                    + 'hover:cursor-not-allowed' : ''}`}
                >
                  <img src={SearchIcon} alt="Search Icon" className="h-4 w-4" />
                </button>
                <div
                  ref={searchRef}
                  className={`outline- flex items-center overflow-hidden transition-all duration-500 ease-in-out ${
                    showSearch ? 'w-[150px] px-2' : 'w-0 px-0'
                  }`}
                >
                  <img
                    onClick={reportData ? toggleSearch : () => null}
                    src={SearchIcon}
                    alt="Search"
                    className={`absolute left-2 h-4 w-4 text-[#828282] ${!reportData || reportData.length === 0 || isFetching
                    || isError ? 'hover:cursor-not-allowed' : ' hover:cursor-pointer'}`}
                  />
                  <input
                    type="text"
                    className={`h-8 w-full rounded border-none py-1 pl-1 pr-2 text-primary outline-none
                     placeholder:text-gray-400 ${
                      showSearch ? 'visible' : 'invisible'
                    }`}
                    placeholder={`${t('Search') || 'Search'}`}
                    onChange={(e) => debouncedSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div
              className="border-gray-300 relative flex items-center rounded border transition-all duration-500 ease-in-out"
            >
              <button
                onClick={() => setOpen(!open)}
                className={`flex h-8 w-8 items-center justify-center rounded  transition-all duration-500
    ease-in-out hover:bg-gray-200 active:bg-[#9EB6BF80] ${
                  open ? 'border border-primary bg-[#9EB6BF80] ' : ''
                }`}
              >
                <img src={ThreeDots} alt="threen dot" className="h-4 w-4" />
              </button>
              {open && (
                <div className="absolute right-0 z-10 mt-60 w-[210px] rounded-[10px] border bg-white shadow-lg">
                  <ul className="gap-3  text-sm text-gray-700">
                    <li className="border py-4 pl-[16px] text-[16px] text-primary"> Edit reports</li>
                    <li className="py-3">
                      <FormControlLabel
                        control={(
                          <SwitchInput
                            checked={isToggledOriginalReferrer}
                          />
                      )}
                        className="relative left-3 flex flex-row-reverse gap-5 pl-[11px] text-[16px] leading-[1.5]
                         text-primary
                        xl:text-[0.6rem]"
                        label={isToggledOriginalReferrer ? t('seeLastEntries') : t('seeEntryPoints')}
                        name="isRequired"
                        onChange={toggleReferrer}
                      />
                    </li>
                    <li className="flex flex-row py-3 pl-[16px] text-[16px]">
                      <ExportButton fetchDownload={reportDownload} />
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex h-[465px] w-full flex-col">
        {!isFetching && !isError && reportData === null && (
        <EmptyBigCard />
        )}
        {isFetching && <LoadingSpinner center color="primary" />}
        {isError && !isFetching && (
        <p>
          {t('errorRetrievingData')}
        </p>
        )}
        {!isFetching && !isError && reportData !== null && (
        <AdsReportTable
          data={referrerReport?.data?.data?.report}
          isFetching={isFetching}
          isError={isError}
          columns={organizationType === OrganizationType.ECOMMERCE
            ? eCommerceReferrerReportColumns
            : referrerReportColumns}
          fixedColumns={referrerReportFixedColumns}
        />
        )}
      </div>
      {!isFetching && !isError && reportData !== null && (
        <div className="mb-[22px] mt-3 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handlePrevPage}
            className={`
              ${page === 0
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10 
            `}
            disabled={page === 0 || isFetching}
          >
            <img src={LeftAngle} alt="" />
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            className={`
              ${!referrerReport?.data?.data?.hasNextPage
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10
            `}
            disabled={!referrerReport?.data?.data?.hasNextPage || isFetching}
          >
            <img src={RightAngle} alt="" />
          </button>
        </div>
      )}
    </div>
  );
};
export default ReferrerReport;
